import {
    AfterViewInit, Component, ComponentRef, ElementRef, EventEmitter,
    Input, NgZone, OnDestroy, Output, ViewChild, ViewContainerRef
  } from '@angular/core';
  
  @Component({
    selector: 'app-floating-host',
    template: `
      <div #root class="kx-floating-host" role="dialog" aria-modal="false">
<div class="ai-title-top" id='aiFloatingHeading' style="display:unset"><span class="ai-star"><img src="/assets/images/ai-icon-star.svg"></span>Scribe</div>
<div class="kx-floating-drag-handle" (pointerdown)="onPointerDown($event)" title="Drag">
<i class="fa fa-arrows" aria-hidden="true"></i>
</div>
<ng-template #contentHost></ng-template>
</div>
    `,
    styleUrls: ['./floating-host.component.scss'],
    standalone: false
})
  export class FloatingHostComponent implements AfterViewInit, OnDestroy {
    @ViewChild('root', { static: true }) rootRef!: ElementRef<HTMLElement>;
    @ViewChild('contentHost', { read: ViewContainerRef, static: true }) contentHost!: ViewContainerRef;
  
    @Input() id = `floating-${Math.random().toString(36).slice(2, 8)}`;
    @Input() snapToEdge = false;
    @Input() persistenceKey?: string;
    // When set, keeps the widget's right edge this many px from the viewport's right edge,
    // re-measuring on every size change (content can grow after this component's own
    // ngAfterViewInit, e.g. a caller injecting real content later) — until the user drags,
    // which takes over as the definitive position.
    @Input() anchorRight?: number;
    // CSS selector of a page area to keep the widget inside (its visible part) while dragging,
    // scrolling or resizing. Falls back to the whole viewport if the area is missing or too small.
    @Input() boundsSelector?: string;
    @Output() destroyed = new EventEmitter<void>();
    @Output() onReady = new EventEmitter<ViewContainerRef>();
  
    public contentRef?: ComponentRef<any>;
  
    private currentX = 10;
    private currentY = 80;
    private rightAnchorActive = false;
    private resizeObserver?: ResizeObserver;
  
    private dragging = false;
    private pointerId: number | null = null;
    private dragStartPointerX = 0;
    private dragStartPointerY = 0;
    private dragStartWidgetX = 0;
    private dragStartWidgetY = 0;
  
    private widgetW = 120;
    private widgetH = 60;
  
    private readonly widgetMargin = 8;
  
    constructor(private ngZone: NgZone) {}
  
    ngAfterViewInit(): void {
      const el = this.rootRef.nativeElement;
      el.style.position = 'fixed';
      el.style.willChange = 'transform';
      if (el.parentElement !== document.body) document.body.appendChild(el);
  
      this.onReady.emit(this.contentHost);
      this.loadPosition();
      this.applyTransform();
      this.updateWidgetSize();
  
      window.addEventListener('resize', this.onResizeBound);
      if (this.boundsSelector) {
        // the bounded area moves when the page scrolls; re-clamp so the widget stays inside it
        this.ngZone.runOutsideAngular(() => window.addEventListener('scroll', this.onScrollBound, true));
      }

      if (this.anchorRight != null) {
        this.rightAnchorActive = true;
        this.applyRightAnchor();
        this.setupContentResizeGuard(el);
      }
    }

    // Only for anchorRight mode: watches the widget's OWN content changing size (e.g. a
    // minimize/maximize toggle inside it). Even after a manual drag disables right-anchoring,
    // the widget must still be kept within the viewport, or a size change while sitting near
    // an edge (dragged near the right, then expanded wider) pushes it off-screen with no way
    // to drag it back since the handle itself would be off-screen too.
    private setupContentResizeGuard(el: HTMLElement) {
      if (typeof ResizeObserver === 'undefined') return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.rightAnchorActive) {
          this.applyRightAnchor();
        } else {
          this.updateWidgetSize();
          this.clampToViewport();
          this.applyTransform();
        }
      });
      this.resizeObserver.observe(el);
    }
  
    ngOnDestroy(): void {
      // 1. Remove event listeners
      window.removeEventListener('resize', this.onResizeBound);
      window.removeEventListener('scroll', this.onScrollBound, true);
      try { this.resizeObserver?.disconnect(); } catch (e) {}
      
      // 2. Destroy the dynamically injected content
      try { this.contentRef?.destroy(); } catch (e) {}
      
      // 3. Remove the host element from the document body
      try { this.rootRef.nativeElement.remove(); } catch (e) {}
  
      // 4. Notify listeners
      this.destroyed.emit();
    }
  
    // ----------------- public API -----------------
    public setPosition(x: number, y: number) {
      this.currentX = x;
      this.currentY = y;
      this.applyTransform();
      this.savePosition();
    }
  
    public getPosition(): { x: number; y: number } {
      return { x: Math.round(this.currentX), y: Math.round(this.currentY) };
    }
  
    public show() { this.rootRef.nativeElement.style.display = ''; }
    public hide() { this.rootRef.nativeElement.style.display = 'none'; }
  
    public destroy() {
      try { this.contentRef?.destroy(); } catch (e) {}
      try { this.rootRef.nativeElement.remove(); } catch (e) {}
      this.ngOnDestroy();
    }
  
    // ----------------- drag handlers -----------------
    onPointerDown(evt: PointerEvent) {
      if (evt.button && evt.button !== 0) return;
  
      const target = evt.target as HTMLElement | null;
      if (target && target.closest('button, a, input, textarea, select, [data-no-drag]')) {
        return; // allow interactive children
      }
  
      const el = this.rootRef.nativeElement;
      try { el.setPointerCapture(evt.pointerId); } catch (e) {}
      this.pointerId = evt.pointerId;
      this.dragging = true;
      this.rightAnchorActive = false; // manual drag takes over as the definitive position
  
      const rect = el.getBoundingClientRect();
      this.dragStartWidgetX = rect.left;
      this.dragStartWidgetY = rect.top;
      this.dragStartPointerX = evt.clientX;
      this.dragStartPointerY = evt.clientY;
  
      this.currentX = this.dragStartWidgetX;
      this.currentY = this.dragStartWidgetY;
  
      this.widgetW = rect.width || this.widgetW;
      this.widgetH = rect.height || this.widgetH;
  
      this.ngZone.runOutsideAngular(() => {
        window.addEventListener('pointermove', this.onPointerMoveBound, { passive: false });
        window.addEventListener('pointerup', this.onPointerUpBound);
      });
  
      evt.preventDefault();
    }
  
    private onPointerMoveBound = (evt: PointerEvent) => this.onPointerMove(evt);
    private onPointerUpBound = (evt: PointerEvent) => this.onPointerUp(evt);
  
    private onPointerMove(evt: PointerEvent) {
      if (!this.dragging || this.pointerId !== evt.pointerId) return;
  
      const dx = evt.clientX - this.dragStartPointerX;
      const dy = evt.clientY - this.dragStartPointerY;
  
      if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
  
      this.currentX = this.dragStartWidgetX + dx;
      this.currentY = this.dragStartWidgetY + dy;
  
      this.clampToViewport();
      this.applyTransform();
  
      evt.preventDefault();
    }
  
    private onPointerUp(evt: PointerEvent) {
      if (!this.dragging || this.pointerId !== evt.pointerId) return;
      this.dragging = false;
  
      try { this.rootRef.nativeElement.releasePointerCapture(evt.pointerId); } catch (e) {}
      window.removeEventListener('pointermove', this.onPointerMoveBound);
      window.removeEventListener('pointerup', this.onPointerUpBound);
  
      if (this.snapToEdge) this.snapToEdgeNow();
  
      this.savePosition();
    }
  
    // ----------------- helpers -----------------
    private applyTransform() {
      const el = this.rootRef.nativeElement;
      el.style.transform = `translate3d(${Math.round(this.currentX)}px, ${Math.round(this.currentY)}px, 0)`;
    }

    private applyRightAnchor() {
      const el = this.rootRef.nativeElement;
      const w = el.getBoundingClientRect().width || this.widgetW;
      this.currentX = window.innerWidth - w - (this.anchorRight || 0);
      this.applyTransform();
    }
  
    private getBounds() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const area = this.boundsSelector ? document.querySelector(this.boundsSelector) : null;
      if (area) {
        const r = area.getBoundingClientRect();
        const b = { left: Math.max(0, r.left), top: Math.max(0, r.top), right: Math.min(vw, r.right), bottom: Math.min(vh, r.bottom) };
        const m2 = this.widgetMargin * 2;
        if (b.right - b.left >= this.widgetW + m2 && b.bottom - b.top >= this.widgetH + m2) return b;
      }
      return { left: 0, top: 0, right: vw, bottom: vh };
    }

    private clampToViewport() {
      const b = this.getBounds();
      const minX = b.left + this.widgetMargin;
      const minY = b.top + this.widgetMargin;
      const maxX = Math.max(minX, b.right - this.widgetW - this.widgetMargin);
      const maxY = Math.max(minY, b.bottom - this.widgetH - this.widgetMargin);
  
      if (this.currentX < minX) this.currentX = minX;
      if (this.currentY < minY) this.currentY = minY;
      if (this.currentX > maxX) this.currentX = maxX;
      if (this.currentY > maxY) this.currentY = maxY;
    }
  
    private updateWidgetSize() {
      const rect = this.rootRef.nativeElement.getBoundingClientRect();
      this.widgetW = rect.width || this.widgetW;
      this.widgetH = rect.height || this.widgetH;
    }
  
    private savePosition() {
      try {
        if (!this.persistenceKey) return;
        localStorage.setItem(this.persistenceKey, JSON.stringify({ x: Math.round(this.currentX), y: Math.round(this.currentY) }));
      } catch (e) {}
    }
  
    private loadPosition() {
      try {
        if (!this.persistenceKey) return;
        const raw = localStorage.getItem(this.persistenceKey);
        if (!raw) return;
        const v = JSON.parse(raw);
        if (typeof v.x === 'number') this.currentX = v.x;
        if (typeof v.y === 'number') this.currentY = v.y;
      } catch (e) {}
    }
  
    private snapToEdgeNow() {
      const el = this.rootRef.nativeElement;
      const centerX = this.currentX + this.widgetW / 2;
      const snapLeft = centerX < window.innerWidth / 2;
      this.currentX = snapLeft ? this.widgetMargin : window.innerWidth - this.widgetW - this.widgetMargin;
      this.applyTransform();
    }
  
    private onScrollBound = () => {
      if (this.dragging || this.rightAnchorActive) return;
      this.updateWidgetSize();
      this.clampToViewport();
      this.applyTransform();
    };

    private onResizeBound = () => {
      this.updateWidgetSize();
      if (this.rightAnchorActive) {
        this.applyRightAnchor();
      } else {
        this.clampToViewport();
        this.applyTransform();
      }
    };
  }
  